<template>
  <View direction="column" gap="md">
    <View v-if="isPending" justify="center" padding-block="lg">
      <LoadingIndicator />
    </View>

    <EmptyState
      v-else-if="inquiries.length === 0"
      icon="file-question-02"
      color="orange"
      title="No one is waiting to apply"
      description="Renters who ask to apply after their tour, or with a reason to skip it, show up here until you answer them."
    />

    <ScrollableHorizontallyContainer v-else>
      <View class="card-items" gap="md" padding-bottom="md">
        <LeadCard
          v-for="inquiry in inquiries"
          :key="inquiry.id"
          :property-inquiry="inquiry"
          class="card-item"
          role="button"
          tabindex="0"
          @click="openPanel(inquiry)"
          @keydown.enter.prevent="openPanel(inquiry)"
          @keydown.space.prevent="openPanel(inquiry)"
        />
      </View>
    </ScrollableHorizontallyContainer>

    <WantsToApplyPanel
      v-if="selectedInquiry"
      ref="panelRef"
      :inquiry="selectedInquiry"
      @closed="selectedInquiry = undefined"
    />
  </View>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';

import { EmptyState, LoadingIndicator, ScrollableHorizontallyContainer, View } from '@tallo/design-system';
import { PropertyInquiryDto, usePropertyWantsToApplyQuery } from '@tallo/investor/property';

import WantsToApplyPanel from '../../../wants-to-apply/wants-to-apply-panel.vue';
import LeadCard from '../leads/lead-card.vue';

const { data, isPending } = usePropertyWantsToApplyQuery();
const inquiries = computed(() => data.value ?? []);

const selectedInquiry = ref<PropertyInquiryDto>();
const panelRef = ref<InstanceType<typeof WantsToApplyPanel>>();

async function openPanel(inquiry: PropertyInquiryDto) {
  selectedInquiry.value = inquiry;
  await nextTick();
  panelRef.value?.open();
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.card-items {
  .card-item {
    width: 28rem;
    flex-shrink: 0;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--t-color-neutral-950);
      outline-offset: 2px;
    }
  }

  @include mobile {
    flex-direction: column;

    .card-item {
      width: 100%;
    }
  }
}
</style>
